import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet } from '../../../utils/fetch';
import { SystemNotificationInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getSystemNotificationsColumns } from './SystemNotificationsColumns';

const SystemNotificationsAdminList: React.FC = () => {
  const [notifications, setNotifications] = useState<SystemNotificationInterface[]>([]);
  const [singleNotification, setSingleNotification] = useState<SystemNotificationInterface | null>(null);
  const [notificationId, setNotificationId] = useState('');
  const [threadId, setThreadId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch all notifications
  const handleGetAllNotifications = async () => {
    setError(null);
    setSingleNotification(null);
    setNotifications([]);
    try {
      const result = await apiGet<GenericResponseInterface<SystemNotificationInterface[]>>('/systemNotifications');
      if (result.status && result.data) {
        setNotifications(result.data);
      } else {
        setError(result.message || 'Failed to fetch notifications');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    }
  };

  // Fetch by ID
  const handleGetNotificationById = async () => {
    setError(null);
    setNotifications([]);
    setSingleNotification(null);
    if (!notificationId) return setError('Please enter a Notification ID');
    try {
      const result = await apiGet<GenericResponseInterface<SystemNotificationInterface>>(`/systemNotifications/byId/${notificationId}`);
      if (result.status && result.data) {
        setSingleNotification(result.data);
      } else {
        setError(result.message || 'Failed to fetch notification by ID');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notification by ID');
    }
  };

  // Fetch by Thread ID
  const handleGetByThreadId = async () => {
    setError(null);
    setNotifications([]);
    setSingleNotification(null);
    if (!threadId) return setError('Please enter a Thread ID');
    try {
      const result = await apiGet<GenericResponseInterface<SystemNotificationInterface[]>>(`/systemNotifications/byThreadId/${threadId}`);
      if (result.status && result.data) {
        setNotifications(result.data);
      } else {
        setError(result.message || 'Failed to fetch by Thread ID');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch by Thread ID');
    }
  };

  const columns = getSystemNotificationsColumns();

  useEffect(() => {
    handleGetAllNotifications();
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleGetAllNotifications}>Get All Notifications</Button>
        <Button variant="contained" onClick={handleGetNotificationById}>Get by ID</Button>
        <TextField
          label="Notification ID"
          value={notificationId}
          onChange={e => setNotificationId(e.target.value)}
          size="small"
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleGetByThreadId}>Get by Thread ID</Button>
        <TextField
          label="Thread ID"
          value={threadId}
          onChange={e => setThreadId(e.target.value)}
          size="small"
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
        {singleNotification ? (
          <DataGrid
            rows={[{ ...singleNotification, id: singleNotification.id ?? Math.random() }]}
            columns={columns}
            hideFooter
            autoHeight
          />
        ) : (
          notifications.length > 0 && (
            <DataGrid
              rows={notifications.map(n => ({ ...n, id: n.id ?? Math.random() }))}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } }
              }}
              pageSizeOptions={[10, 25, 50]}
              autoHeight
            />
          )
        )}
      </Paper>
    </Box>
  );
};

export default SystemNotificationsAdminList;